import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  fetchClassworkPipelineReview,
  type ClassworkPipelineRegion,
  type ClassworkPipelineReviewData,
  type ClassworkPipelineReviewPage
} from '../../lib/classworkApi';

type Props = {
  classworkId: number;
  assignmentId: number | null;
  open: boolean;
  onClose: () => void;
};

function normalizeBbox(region: ClassworkPipelineRegion): { x: number; y: number; w: number; h: number } | null {
  const raw = region?.bbox && typeof region.bbox === 'object' ? region.bbox : null;
  if (!raw) return null;
  const x = Number(raw.x ?? raw.left ?? raw.xMin ?? raw.x0);
  const y = Number(raw.y ?? raw.top ?? raw.yMin ?? raw.y0);
  const w = Number(raw.width ?? raw.w ?? raw.xMax - raw.xMin);
  const h = Number(raw.height ?? raw.h ?? raw.yMax - raw.yMin);
  if (![x, y, w, h].every((v) => Number.isFinite(v))) return null;
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
    w: Math.max(0, Math.min(1, w)),
    h: Math.max(0, Math.min(1, h))
  };
}

function RegionOverlay({
  imageUrl,
  docUrl,
  regions,
  title
}: {
  imageUrl?: string;
  docUrl?: string;
  regions: ClassworkPipelineRegion[];
  title: string;
}) {
  const bboxes = useMemo(() => {
    return (regions || [])
      .map((r) => ({ region: r, bbox: normalizeBbox(r) }))
      .filter((x) => x.bbox != null) as Array<{ region: ClassworkPipelineRegion; bbox: { x: number; y: number; w: number; h: number } }>;
  }, [regions]);
  if (!imageUrl && !docUrl) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-6 text-sm text-slate-600">
        Keine Dokumentansicht verfügbar.
      </div>
    );
  }
  if (!imageUrl) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-600">{title}</div>
        <iframe src={docUrl} className="h-[300px] w-full rounded-lg border border-slate-300 bg-white md:h-[360px]" title={title} />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-slate-600">{title}</div>
      <div className="relative h-[300px] w-full overflow-hidden rounded-lg border border-slate-300 bg-slate-100 md:h-[360px]">
        <img src={imageUrl} alt={title} className="h-full w-full object-contain" />
        {bboxes.map((entry, idx) => (
          <div
            key={`${entry.region.regionKey || 'box'}-${idx}`}
            className="absolute border-2 border-emerald-500/90 bg-emerald-400/10"
            style={{
              left: `${entry.bbox.x * 100}%`,
              top: `${entry.bbox.y * 100}%`,
              width: `${entry.bbox.w * 100}%`,
              height: `${entry.bbox.h * 100}%`
            }}
            title={`${entry.region.taskId || '?'} (${entry.region.regionType || 'region'})`}
          />
        ))}
      </div>
      {bboxes.length === 0 ? (
        <div className="text-xs text-slate-500">
          Keine geometrischen Bounding-Boxes vorhanden — es werden nur textuelle Regionsignale angezeigt.
        </div>
      ) : null}
    </div>
  );
}

function RegionsList({
  title,
  items
}: {
  title: string;
  items: Array<ClassworkPipelineRegion | { taskId?: string | null; regionKey?: string | null; cropText?: string | null; confidence?: number | null }>;
}) {
  return (
    <div className="flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-700">{title}</div>
      <div className="min-h-0 flex-1 space-y-2 overflow-auto pr-1">
        {(items || []).length === 0 ? (
          <div className="text-xs text-slate-500">Keine Einträge.</div>
        ) : (
          items.map((it: any, idx) => (
            <div key={`${it?.regionKey || it?.taskId || 'row'}-${idx}`} className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs">
              <div className="font-medium text-slate-800">
                {it?.taskId || 'ohne Task-ID'} · {it?.regionType || 'region'}
              </div>
              {it?.anchorText ? <div className="text-slate-700">{String(it.anchorText).slice(0, 180)}</div> : null}
              {it?.cropText ? <div className="text-slate-700">{String(it.cropText).slice(0, 180)}</div> : null}
              {it?.confidence != null ? (
                <div className="text-slate-500">Conf: {Math.round(Number(it.confidence) * 100)}%</div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ClassworkPipelineReviewModal({ classworkId, assignmentId, open, onClose }: Props) {
  const [tab, setTab] = useState<'reference' | 'submission'>('submission');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const [selectedPageKey, setSelectedPageKey] = useState<string>('');

  const query = useQuery<ClassworkPipelineReviewData>(
    ['classwork-pipeline-review', classworkId, assignmentId],
    () => fetchClassworkPipelineReview(classworkId, Number(assignmentId)),
    {
      enabled: open && Number.isFinite(Number(assignmentId)) && Number(assignmentId) > 0
    }
  );

  const data = query.data;
  const submissions = Array.isArray(data?.submissions) ? data!.submissions : [];
  const referencePages = Array.isArray(data?.referencePages) ? data!.referencePages : [];

  const activeSubmissionId = selectedSubmissionId ?? (submissions[0]?.submissionId ?? null);
  const activeSubmission = submissions.find((s) => s.submissionId === activeSubmissionId) || null;

  const pages = tab === 'reference' ? referencePages : activeSubmission?.pages || [];
  const activePage =
    pages.find((p) => `${p.sourceIndex}:${p.sourceLabel || ''}` === selectedPageKey) ||
    pages[0] ||
    null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4">
      <div className="flex h-[94vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Pipeline-Überprüfung</h3>
            <div className="text-xs text-slate-600">
              Transparenzansicht für Region-/Crop-Erkennung · Assignment {assignmentId}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-100">
            Schließen
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-12 gap-3 p-4">
          <div className="col-span-12 min-h-0 space-y-3 overflow-auto rounded-xl border border-slate-200 bg-white p-3 lg:col-span-3">
            <div className="flex gap-1 rounded bg-slate-100 p-1 text-xs">
              <button
                type="button"
                onClick={() => setTab('submission')}
                className={`flex-1 rounded px-2 py-1 ${tab === 'submission' ? 'bg-white font-semibold text-slate-900' : 'text-slate-600'}`}
              >
                Abgaben
              </button>
              <button
                type="button"
                onClick={() => setTab('reference')}
                className={`flex-1 rounded px-2 py-1 ${tab === 'reference' ? 'bg-white font-semibold text-slate-900' : 'text-slate-600'}`}
              >
                Musterlösung
              </button>
            </div>

            {tab === 'submission' ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Attempts</div>
                <div className="space-y-1">
                  {submissions.map((s) => (
                    <button
                      key={s.submissionId}
                      type="button"
                      onClick={() => {
                        setSelectedSubmissionId(s.submissionId);
                        setSelectedPageKey('');
                      }}
                      className={`w-full rounded border px-2 py-1.5 text-left text-xs ${
                        activeSubmissionId === s.submissionId
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      #{s.attemptNo ?? '?'} · {s.analysisStatus || 'n/a'}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-2 text-xs text-slate-700">
                Referenz-Version: {data?.referenceSetVersionId ?? 'n/a'}
              </div>
            )}

            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Seiten</div>
              <div className="max-h-72 space-y-1 overflow-auto pr-1">
                {pages.map((p) => {
                  const key = `${p.sourceIndex}:${p.sourceLabel || ''}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedPageKey(key)}
                      className={`w-full rounded border px-2 py-1.5 text-left text-xs ${
                        activePage === p
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {p.sourceLabel || `Seite ${p.sourceIndex}`}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-span-12 min-h-0 lg:col-span-9">
            {query.isLoading ? (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-600">Lade Pipeline-Daten …</div>
            ) : query.isError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-8 text-sm text-rose-800">
                Pipeline-Daten konnten nicht geladen werden.
              </div>
            ) : !activePage ? (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-600">
                Keine Seiten mit Zerlegungsdaten gefunden.
              </div>
            ) : (
              <div className="flex h-full min-h-0 flex-col gap-3">
                <div className="shrink-0 rounded-xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 text-sm font-semibold text-slate-900">{activePage.sourceLabel || `Seite ${activePage.sourceIndex}`}</div>
                  {activePage.summary ? <div className="mb-2 text-xs text-slate-600">{activePage.summary}</div> : null}
                  {activePage.viewUrl ? (
                    <div className="mb-2">
                      <a href={activePage.viewUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 underline">
                        Originaldokument öffnen
                      </a>
                    </div>
                  ) : null}
                  <RegionOverlay
                    title={activePage.originalName || activePage.sourceLabel || `Seite ${activePage.sourceIndex}`}
                    imageUrl={activePage.previewUrl}
                    docUrl={activePage.viewUrl}
                    regions={[...(activePage.regions || []), ...(activePage.templateRegions || [])]}
                  />
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-3">
                  <RegionsList title="Regionen" items={activePage.regions || []} />
                  <RegionsList title="Template-Regionen" items={activePage.templateRegions || []} />
                  <RegionsList title="Crop-Ergebnisse" items={activePage.cropResults || []} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
